import ApiClient from './ApiClient';

class QuotesAPI extends ApiClient {
  constructor() {
    super('quotes', { accountScoped: true });
  }

  get(params = {}) {
    return axios.get(this.url, { params });
  }

  getAll(params = {}) {
    return axios.get(this.url, { params });
  }

  show(quoteId) {
    return axios.get(`${this.url}/${quoteId}`);
  }

  create(data) {
    return axios.post(this.url, data);
  }

  update(quoteId, data) {
    return axios.patch(`${this.url}/${quoteId}`, data);
  }

  delete(quoteId) {
    return axios.delete(`${this.url}/${quoteId}`);
  }

  markAsSold(quoteId, payments = []) {
    return axios.post(`${this.url}/${quoteId}/mark_as_sold`, { payments });
  }

  reopenSale(quoteId) {
    return axios.post(`${this.url}/${quoteId}/reopen_sale`);
  }

  updatePayments(quoteId, payments = []) {
    return axios.put(`${this.url}/${quoteId}/update_payments`, { payments });
  }

  getPayments(quoteId) {
    return axios.get(`${this.url}/${quoteId}/payments`);
  }

  getDashboard(params = {}) {
    return axios.get(`${this.url}/dashboard`, { params });
  }

  getSalesReport(params = {}) {
    return axios.get(`${this.url}/sales_report`, { params });
  }

  getDeliveryReport(params = {}) {
    return axios.get(`${this.url}/delivery_report`, { params });
  }

  markAsSeparated(
    quoteId,
    { deliveryType, deliveryPersonId, deliveryPersonName, deliveryPersonPhone }
  ) {
    return axios.post(`${this.url}/${quoteId}/mark_as_separated`, {
      delivery_type: deliveryType,
      delivery_person_id: deliveryPersonId,
      delivery_person_name: deliveryPersonName,
      delivery_person_phone: deliveryPersonPhone,
    });
  }

  changeDeliveryType(quoteId, deliveryType) {
    return axios.post(`${this.url}/${quoteId}/change_delivery_type`, {
      delivery_type: deliveryType,
    });
  }

  markAsOutForDelivery(
    quoteId,
    { deliveryPersonId, deliveryPersonName, deliveryPersonPhone } = {}
  ) {
    return axios.post(`${this.url}/${quoteId}/mark_as_out_for_delivery`, {
      delivery_person_id: deliveryPersonId,
      delivery_person_name: deliveryPersonName,
      delivery_person_phone: deliveryPersonPhone,
    });
  }

  markAsDelivered(quoteId) {
    return axios.post(`${this.url}/${quoteId}/mark_as_delivered`);
  }

  markAsCancelled(quoteId, { reason } = {}) {
    return axios.post(`${this.url}/${quoteId}/mark_as_cancelled`, { reason });
  }

  undoStatus(quoteId) {
    return axios.post(`${this.url}/${quoteId}/undo_status`);
  }

  resendDriverNotification(quoteId) {
    return axios.post(`${this.url}/${quoteId}/resend_driver_notification`);
  }

  notifyPickupReady(quoteId) {
    return axios.post(`${this.url}/${quoteId}/notify_pickup_ready`);
  }

  markDeliveryAsPaid(quoteId) {
    return axios.post(`${this.url}/${quoteId}/mark_delivery_as_paid`);
  }

  markDeliveryAsUnpaid(quoteId) {
    return axios.post(`${this.url}/${quoteId}/mark_delivery_as_unpaid`);
  }

  batchMarkDeliveryAsPaid(quoteIds) {
    return axios.post(`${this.url}/batch_mark_delivery_as_paid`, {
      quote_ids: quoteIds,
    });
  }

  // Promissoria
  getPromissoriaEmployees() {
    return axios.get(`${this.url}/promissoria_employees`);
  }

  getPromissoriaEmployeesWithCredit(params = {}) {
    return axios.get(`${this.url}/promissoria_employees_with_credit`, { params });
  }

  getPromissoriaReport(params = {}) {
    return axios.get(`${this.url}/promissoria_report`, { params });
  }

  getPromissoriaAnalytics() {
    return axios.get(`${this.url}/promissoria_analytics`);
  }

  markPromissoriaPaid(quoteId, paymentId) {
    return axios.post(`${this.url}/${quoteId}/mark_promissoria_paid`, {
      payment_id: paymentId,
    });
  }

  markPromissoriaUnpaid(quoteId, paymentId) {
    return axios.post(`${this.url}/${quoteId}/mark_promissoria_unpaid`, {
      payment_id: paymentId,
    });
  }

  batchMarkPromissoriaPaid(paymentIds) {
    return axios.post(`${this.url}/batch_mark_promissoria_paid`, {
      payment_ids: paymentIds,
    });
  }

  // Cash Closings
  getCashClosings(params = {}) {
    return axios.get(`${this.url.replace('/quotes', '')}/cash_closings`, { params });
  }

  createCashClosing(data) {
    return axios.post(`${this.url.replace('/quotes', '')}/cash_closings`, data);
  }

  deleteCashClosing(id) {
    return axios.delete(
      `${this.url.replace('/quotes', '')}/cash_closings/${id}`
    );
  }

  // Cash Registers
  getCashRegisters(params = {}) {
    return axios.get(
      `${this.url.replace('/quotes', '')}/cash_registers`,
      { params }
    );
  }

  createCashRegister(data) {
    return axios.post(
      `${this.url.replace('/quotes', '')}/cash_registers`,
      data
    );
  }

  closeCashRegister(id) {
    return axios.post(
      `${this.url.replace('/quotes', '')}/cash_registers/${id}/close`
    );
  }

  getCurrentCashRegister() {
    return axios.get(
      `${this.url.replace('/quotes', '')}/cash_registers/current`
    );
  }

  getClosedRegistersForReview(params = {}) {
    return axios.get(
      `${this.url.replace('/quotes', '')}/cash_registers/closed_for_review`,
      { params }
    );
  }

  // Items
  getItems(quoteId) {
    return axios.get(`${this.url}/${quoteId}/items`);
  }

  createItem(quoteId, data) {
    return axios.post(`${this.url}/${quoteId}/items`, data);
  }

  updateItem(quoteId, itemId, data) {
    return axios.patch(`${this.url}/${quoteId}/items/${itemId}`, data);
  }

  deleteItem(quoteId, itemId) {
    return axios.delete(`${this.url}/${quoteId}/items/${itemId}`);
  }

  reorderItems(quoteId, positions) {
    return axios.post(`${this.url}/${quoteId}/items/reorder`, { positions });
  }

  reprint(quoteId, printer = 'sale_receipt') {
    return axios.post(`${this.url}/${quoteId}/reprint`, { printer });
  }

  analyzeImage(quoteId, file) {
    const formData = new FormData();
    formData.append('image', file);
    return axios.post(`${this.url}/${quoteId}/analyze_image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }
}

export default new QuotesAPI();
